import { FC as ReactFC, useContext, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import BlockUi from 'react-block-ui';
import * as intl from 'react-intl-universal';
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import AppContext from 'context/AppContext';
import ImageComponent from 'shared/components/image/ImageComponent';
import Status from 'shared/enums/Status';
import MfaDeactivateIllustration from 'shared/static/img/mfa-deactivate.svg';
import MfaDeactivateThumb from 'shared/static/img/thumbs/mfa-deactivate.png';

import MfaTrustedDevicesList from './mfa-trusted-devices-list/MfaTrustedDevicesList';
import styles from './mfaTrustedDevices.module.scss';
import MfaTrustedDevicesProps from './MfaTrustedDevicesProps';

const MfaTrustedDevices: ReactFC<MfaTrustedDevicesProps> = (
  props: MfaTrustedDevicesProps
) => {
  const {
    trustedDevices,
    status,
    onRefetchTrustedDevices,
    onRevokeTrustedDevice,
    onRevokeAllTrustedDevices,
  } = props;

  const [focusedRevocableDevice, setFocusedRevocableDevice] =
    useState<string>('');
  const [isSingleRevokeModalOpen, setIsSingleRevokeModalOpen] =
    useState<boolean>(false);
  const [isAllRevokeModalOpen, setIsAllRevokeModalOpen] =
    useState<boolean>(false);
  const { setErrorToastText } = useContext(AppContext);

  const onToggleSingleRevokeModal = (): void => {
    setIsSingleRevokeModalOpen((prev) => !prev);
  };

  const onToggleAllRevokeModal = (): void => {
    setIsAllRevokeModalOpen((prev) => !prev);
  };

  const handleRevokeSingleDevice = (deviceId: string): void => {
    setFocusedRevocableDevice(deviceId);
    setIsSingleRevokeModalOpen(true);
    setIsAllRevokeModalOpen(false);
  };

  const handleRevokeAllDevices = (): void => {
    setIsAllRevokeModalOpen(true);
    setIsSingleRevokeModalOpen(false);
  };

  const handleRevokeConfirmationForSingleDevice = async (): Promise<void> => {
    if (isEmpty(focusedRevocableDevice)) {
      setErrorToastText(intl.get('ERR_MFA_TRUSTED_DEVICE_NOT_AVAILABLE'));
    } else {
      const { status: actionStatus } = await onRevokeTrustedDevice(
        focusedRevocableDevice
      );
      if (actionStatus === Status.Success) {
        setIsSingleRevokeModalOpen(false);
        setIsAllRevokeModalOpen(false);
        await onRefetchTrustedDevices();
      }
    }
  };

  const handleRevokeConfirmationForAllDevices = async (): Promise<void> => {
    const { status: actionStatus } = await onRevokeAllTrustedDevices();
    if (actionStatus === Status.Success) {
      setIsSingleRevokeModalOpen(false);
      setIsAllRevokeModalOpen(false);
      await onRefetchTrustedDevices();
    }
  };

  const renderSingleDeviceRevokeConfirmationModal = (): JSX.Element => (
    <Modal
      size="md"
      isOpen={isSingleRevokeModalOpen}
      toggle={onToggleSingleRevokeModal}
      backdrop="static"
      centered
      autoFocus={false}
      trapFocus
      keyboard={false}
      id="warningPromptTarget"
    >
      <BlockUi tag="div" blocking={status === Status.Loading}>
        <ModalHeader toggle={onToggleSingleRevokeModal}>
          {intl.get('LBL_MFA_TRUSTED_DEVICES_REVOKE_SINGLE_TITLE')}
        </ModalHeader>
        <ModalBody>
          <Col xs="12">
            <Row className="d-flex flex-column align-items-center mb-3">
              <ImageComponent
                loading="eager"
                src={MfaDeactivateIllustration}
                className={`${styles.mfaRevokeImg}`}
                alt="MFA Deactivate"
                thumb={MfaDeactivateThumb}
              />
            </Row>
            <Row className="mb-3">
              <span className="text-14-light">
                {intl.get('LBL_MFA_TRUSTED_DEVICES_REVOKE_SINGLE_DESCRIPTION')}
              </span>
            </Row>
          </Col>
        </ModalBody>
        <ModalFooter className="p-3">
          <Col xs="12" className="d-flex justify-content-between">
            <Button color="secondary" onClick={onToggleSingleRevokeModal}>
              {intl.get('BTN_CANCEL')}
            </Button>

            <Button
              color="primary"
              onClick={handleRevokeConfirmationForSingleDevice}
            >
              {intl.get('BTN_MFA_REVOKE_ACCESS')}
            </Button>
          </Col>
        </ModalFooter>
      </BlockUi>
    </Modal>
  );

  const renderAllDeviceRevokeConfirmationModal = (): JSX.Element => (
    <Modal
      size="md"
      isOpen={isAllRevokeModalOpen}
      toggle={onToggleAllRevokeModal}
      backdrop="static"
      centered
      autoFocus={false}
      trapFocus
      keyboard={false}
      id="warningPromptTarget"
    >
      <BlockUi tag="div" blocking={status === Status.Loading}>
        <ModalHeader toggle={onToggleAllRevokeModal}>
          {intl.get('LBL_MFA_TRUSTED_DEVICES_REVOKE_ALL_TITLE')}
        </ModalHeader>
        <ModalBody>
          <Col xs="12">
            <Row className="d-flex flex-column align-items-center mb-3">
              <ImageComponent
                loading="eager"
                src={MfaDeactivateIllustration}
                className={`${styles.mfaRevokeImg}`}
                alt="MFA Deactivate"
                thumb={MfaDeactivateThumb}
              />
            </Row>
            <Row className="mb-3">
              <span className="text-14-light">
                {intl.get('LBL_MFA_TRUSTED_DEVICES_REVOKE_ALL_DESCRIPTION')}
              </span>
            </Row>
          </Col>
        </ModalBody>
        <ModalFooter className="p-3">
          <Col xs="12" className="d-flex justify-content-between">
            <Button color="secondary" onClick={onToggleAllRevokeModal}>
              {intl.get('BTN_CANCEL')}
            </Button>

            <Button
              color="primary"
              onClick={handleRevokeConfirmationForAllDevices}
            >
              {intl.get('BTN_MFA_REVOKE_ACCESS')}
            </Button>
          </Col>
        </ModalFooter>
      </BlockUi>
    </Modal>
  );

  return (
    <BlockUi
      tag="div"
      blocking={status === Status.Loading}
      className="row dashboard-row"
    >
      <>
        <Col>
          <div className="content-box lower-margins">
            <div className="content-box-title align-items-start justify-content-between pb-4 pt-2">
              <Col className="d-flex flex-column align-items-start p-0">
                <h3 className="main-title">
                  {intl.get('LBL_MFA_TRUSTED_DEVICES_SECTION_TITLE')}
                </h3>
                <p className="text-14-light text-gray mb-0 mt-2">
                  {intl.get('LBL_MFA_TRUSTED_DEVICES_SECTION_DESCRIPTION')}
                </p>
              </Col>
              <Col className="d-flex flex-column align-items-end p-0">
                <Button
                  className="btn btn-secondary"
                  onClick={handleRevokeAllDevices}
                  disabled={!trustedDevices.length}
                >
                  {intl.get('BTN_MFA_REVOKE_ALL')}
                </Button>
              </Col>
            </div>
            <Row className="d-flex flex-wrap justify-content-start">
              <Col xs="auto" className="mb-4 w-100">
                <MfaTrustedDevicesList
                  trustedDevices={trustedDevices}
                  status={status}
                  onRefetchTrustedDevices={onRefetchTrustedDevices}
                  onRevokeTrustedDevice={handleRevokeSingleDevice}
                />
              </Col>
            </Row>
          </div>
        </Col>
        {isSingleRevokeModalOpen && renderSingleDeviceRevokeConfirmationModal()}
        {isAllRevokeModalOpen && renderAllDeviceRevokeConfirmationModal()}
      </>
    </BlockUi>
  );
};

export default MfaTrustedDevices;
